/** Pick format for MediaRecorder (file saved as-is) */
export function getRecordingMime(): {
  recordMime: string;
  blobMime: string;
  extension: string;
} {
  const candidates: Array<{ record: string; blob: string; ext: string }> = [
    { record: 'audio/webm', blob: 'audio/webm', ext: 'webm' },
    { record: 'audio/webm;codecs=opus', blob: 'audio/webm', ext: 'webm' },
    { record: 'audio/mp4', blob: 'audio/mp4', ext: 'm4a' },
    { record: 'audio/ogg', blob: 'audio/ogg', ext: 'ogg' },
    { record: 'audio/ogg;codecs=opus', blob: 'audio/ogg', ext: 'ogg' },
  ];

  for (const c of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c.record)) {
      return { recordMime: c.record, blobMime: c.blob, extension: c.ext };
    }
  }

  return { recordMime: 'audio/webm', blobMime: 'audio/webm', extension: 'webm' };
}

export function buildRecordingFile(
  chunks: Blob[],
  blobMime: string,
  extension: string,
): File | null {
  if (chunks.length === 0) return null;
  const blob = new Blob(chunks, { type: blobMime });
  // Minimal size check — even tiny recordings are valid if they have data
  if (blob.size === 0) return null;
  return new File([blob], `recording_${Date.now()}.${extension}`, { type: blobMime });
}

/** WAV plays reliably in <audio> — use for preview only */
function encodeWav(audioBuffer: AudioBuffer): ArrayBuffer {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const format = 1;
  const bitDepth = 16;

  const samples = audioBuffer.length;
  const blockAlign = (numChannels * bitDepth) / 8;
  const byteRate = sampleRate * blockAlign;
  const dataSize = samples * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, format, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < samples; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = Math.max(-1, Math.min(1, audioBuffer.getChannelData(ch)[i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }

  return buffer;
}

/** Decode recorded file to WAV blob URL for in-browser preview */
export async function createPreviewPlaybackUrl(file: File): Promise<string> {
  // Fallback: if we can't decode, just use the original blob URL
  try {
    const arrayBuffer = await file.arrayBuffer();
    const ctx = new AudioContext();
    try {
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
      const wav = encodeWav(audioBuffer);
      return URL.createObjectURL(new Blob([wav], { type: 'audio/wav' }));
    } finally {
      await ctx.close();
    }
  } catch {
    // Return original file as a blob URL for direct playback
    return URL.createObjectURL(file);
  }
}
